import * as React from 'react';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSnackbar} from 'notistack';

import {
  ERROR,
  NO_RESULT_USER_SEARCH,
  REQUEST_CANCELED,
  NO_PARAMETER_USER_SEARCH,
  REGEX_MASSSEARCH_PATTERN,
} from '../constants';
import {useAuth} from './auth';
import {searchUser} from '../api';
import {
  UserGroupResponseDto,
  UserResult,
  SearchValues,
  UserContextValue,
} from '../models';

const initialResult: UserResult = {
  content: [],
  page: {totalElements: 0, totalPages: 0, number: 0, size: 0},
};

export const UserContext = React.createContext<UserContextValue>({
  text: '',
  sort: '',
  result: initialResult,
  userGroups: [],
  searching: false,
  changeValues: () => {},
  page: 0,
  pageSize: 30,
  resetUserSearch: () => {},
  setResult(_user) {},
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [text, setText] = useState<string>('');
  const [userGroups, setUserGroups] = useState<UserGroupResponseDto[]>([]);
  const [sort, setSort] = useState<string>('');
  const [result, setResult] = useState<UserResult>(initialResult);
  const [searching, setSearching] = useState<boolean>(false);
  const [startSearch, setStartSearch] = useState<boolean>(false);
  const [page, setPage] = useState<number | undefined>(0);
  const [pageSize, setPageSize] = useState<number | undefined>(30);
  const {enqueueSnackbar} = useSnackbar();
  const {token, forceLogout} = useAuth();
  const [controller, setController] = useState<AbortController>(
    new AbortController(),
  );

  const resetUserSearch = useCallback(() => {
    controller.abort();
    setSearching(false);
    setPage(0);
    setText('');
    setResult(initialResult);
    setSort('');
    setUserGroups([]);
  }, [controller]);

  const search = () => {
    setSearching(true);
    const newController = new AbortController();
    const groups = userGroups.map(group => group.id);
    setController(newController);
    let data: UserResult;
    searchUser(
      groups,
      text.trim().split(REGEX_MASSSEARCH_PATTERN).join(';'),
      token,
      newController.signal,
      pageSize,
      page,
      sort,
    )
      .then(res => {
        data = res.data;
        if (data.page.totalElements > 0) {
          setResult(data);
        } else {
          enqueueSnackbar(NO_RESULT_USER_SEARCH, {variant: 'info'});
          setResult(data);
        }
        setSearching(false);
      })
      .catch(err => {
        if (err?.code === 'ERR_CANCELED') {
          enqueueSnackbar(REQUEST_CANCELED, {variant: 'info'});
        } else {
          switch (err?.response?.status) {
            case 401:
              forceLogout();
              break;
            default:
              enqueueSnackbar(ERROR, {variant: 'error'});
              setResult(initialResult);
          }
          setSearching(false);
        }
      });
  };

  const changeValues = useCallback((values: SearchValues) => {
    let search = true;

    if (values.userGroups !== undefined) {
      setUserGroups(values.userGroups);
      setPage(0);
      setSort('');
    }
    if (values.text !== undefined) {
      setText(values.text);
      setPage(0);
      setSort('');
      search = false;
    }
    if (values.page || values.page === 0) {
      setPage(values.page);
    }
    if (values.pageSize) {
      setPageSize(values.pageSize);
      setPage(0);
    }
    if (values.sort) {
      setSort('&sort=' + values.sort);
    }
    setStartSearch(search);
  }, []);

  // User search effect with intentionally limited dependencies
  useEffect(() => {
    if (startSearch) {
      if (!searching && (text !== '' || userGroups.length > 0)) {
        search();
      } else {
        enqueueSnackbar(NO_PARAMETER_USER_SEARCH, {variant: 'warning'});
      }
    }
    setStartSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSearch]);

  const value = useMemo(
    () => ({
      userGroups,
      searching,
      text,
      sort,
      result,
      page,
      pageSize,
      changeValues,
      resetUserSearch,
      setResult,
    }),
    [
      userGroups,
      sort,
      searching,
      text,
      result,
      page,
      pageSize,
      changeValues,
      resetUserSearch,
      setResult,
    ],
  );
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
